import { ErrorHandler } from '@angular/core';
import * as Sentry from '@sentry/angular';

/**
 * The widget's own Sentry client, isolated from the page it is embedded in.
 *
 * The widget runs inside client sites, so `Sentry.init` is never called: it would
 * install global `window.onerror` / `unhandledrejection` handlers, wrap the page's
 * timers and listeners, and record the page's breadcrumbs, so every JS error on the
 * host site would be reported as ours (and would clash with a host page's own
 * Sentry). Instead the widget keeps a private client + scope (Sentry's "shared
 * environments" setup) and only reports what is captured through it: Angular's
 * ErrorHandler and explicit `widgetSentry.scope?.captureException(...)` calls.
 *
 * Tracing and Session Replay are not used: both instrument the whole page
 * (fetch/XHR, navigation, the DOM), so they would record the host site, not the widget.
 */

/** Default integrations that hook global state — the host page's errors and activity. */
const GLOBAL_INTEGRATION_NAMES = [
  'BrowserApiErrors',
  'BrowserSession',
  'Breadcrumbs',
  'ConversationId',
  'GlobalHandlers',
  'FunctionToString',
];

/**
 * The widget bundle's URL, read while it is executing. Used as `allowUrls`, so an
 * error whose stack trace never touches the widget's code is dropped even if it
 * reaches the client. A classic `<script>` exposes it as `document.currentScript`;
 * a module script doesn't, so it's read from this code's own stack frame instead.
 * Null when neither works: then nothing is filtered by URL.
 */
const widgetScriptUrl: string | null = (() => {
  if (typeof document !== 'undefined' && document.currentScript instanceof HTMLScriptElement && document.currentScript.src) {
    return document.currentScript.src;
  }
  const ownStackFrameUrl = new Error().stack?.match(/(https?:\/\/[^\s)]+?\.js)/);
  return ownStackFrameUrl ? ownStackFrameUrl[1] : null;
})();

export interface WidgetSentryOptions {
  dsn: string;
  environment: string;
}

export const widgetSentry: { scope: Sentry.Scope | null } = { scope: null };

/** Starts the widget's private Sentry client. Does nothing without a DSN. */
export function initWidgetSentry(options: WidgetSentryOptions): void {
  if (!options.dsn || widgetSentry.scope !== null) {
    return;
  }

  const widgetClient = new Sentry.BrowserClient({
    dsn: options.dsn,
    environment: options.environment,
    transport: Sentry.makeFetchTransport,
    stackParser: Sentry.defaultStackParser,
    integrations: Sentry.getDefaultIntegrations({}).filter(
      (defaultIntegration) => !GLOBAL_INTEGRATION_NAMES.includes(defaultIntegration.name),
    ),
    allowUrls: widgetScriptUrl ? [widgetScriptUrl] : undefined,
    // Send default PII (e.g. automatic IP address collection) with events.
    sendDefaultPii: true,
    enableLogs: true,
  });

  const widgetScope = new Sentry.Scope();
  widgetScope.setClient(widgetClient);
  widgetClient.init();
  widgetSentry.scope = widgetScope;
}

/**
 * Angular's ErrorHandler, reporting to the widget's own client. (Sentry's
 * `createErrorHandler()` reports to the global client, which the widget no longer
 * creates.) Reuses its error extraction — unwrapping Angular and HTTP errors.
 * Provided with `useValue`, not through DI.
 */
export class WidgetSentryErrorHandler extends Sentry.SentryErrorHandler implements ErrorHandler {
  override handleError(error: unknown): void {
    widgetSentry.scope?.captureException(this._extractError(error), {
      mechanism: { type: 'angular', handled: false },
    });
    console.error(error);
  }
}
